import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";

const DemoCheck = () => {
    const [domain, setDomain] = useState("");
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null);
    const [error, setError] = useState("");

    const handleScan = async (e) => {
        e.preventDefault();
        if (!domain) return;

        setLoading(true);
        setError("");
        setReport(null);

        try {
            // Using the public scan endpoint
            const res = await api.get(`/api/scan?domain=${domain}`);
            setReport(res.data);
        } catch (err) {
            setError("Failed to scan domain. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mt-5 mb-5">
            <div className="text-center mb-5">
                <h1 className="display-4 fw-bold text-primary">
                    <i className="las la-shield-alt me-2"></i>
                    Quick Security Check
                </h1>
                <p className="lead text-muted">
                    Instantly analyze your website's security posture.
                </p>
            </div>

            <div className="row justify-content-center mb-5">
                <div className="col-md-8">
                    <form onSubmit={handleScan} className="d-flex gap-2">
                        <input
                            type="text"
                            className="form-control form-control-lg"
                            placeholder="Enter domain (e.g., example.com)"
                            value={domain}
                            onChange={(e) => setDomain(e.target.value)}
                        />
                        <button
                            type="submit"
                            className="btn btn-primary btn-lg px-4"
                            disabled={loading}
                        >
                            {loading ? (
                                <span><i className="las la-spinner la-spin me-2"></i>Scanning...</span>
                            ) : (
                                <span><i className="las la-search me-2"></i>Scan</span>
                            )}
                        </button>
                    </form>
                    {error && <div className="alert alert-danger mt-3">{error}</div>}
                </div>
            </div>

            {report && (
                <div className="row g-4">
                    {/* Left Column: Minimal Report */}
                    <div className="col-md-6">
                        <div className="card shadow-sm h-100 border-0">
                            <div className="card-header bg-white border-0 pt-4 px-4">
                                <h4 className="card-title mb-0 text-primary">
                                    <i className="las la-chart-bar me-2"></i>
                                    Scan Results
                                </h4>
                            </div>
                            <div className="card-body px-4">
                                <div className="mb-4">
                                    <h6 className="text-uppercase text-muted small fw-bold">Security Grade</h6>
                                    <div className={`display-1 fw-bold text-${report.grade === 'A' || report.grade === 'A-' ? 'success' : report.grade === 'B' ? 'warning' : 'danger'}`}>
                                        {report.grade || 'N/A'}
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <h6 className="text-uppercase text-muted small fw-bold">HTTPS Support</h6>
                                    <div className="d-flex align-items-center mt-2">
                                        {report.protocols?.["TLSv1.3"] ? (
                                            <span className="badge bg-success me-2 p-2">TLS 1.3</span>
                                        ) : (
                                            <span className="badge bg-secondary me-2 p-2">TLS 1.3</span>
                                        )}
                                        {report.protocols?.["TLSv1.2"] ? (
                                            <span className="badge bg-success me-2 p-2">TLS 1.2</span>
                                        ) : (
                                            <span className="badge bg-warning text-dark me-2 p-2">TLS 1.2</span>
                                        )}
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <h6 className="text-uppercase text-muted small fw-bold">Certificate</h6>
                                    <p className="mb-1">
                                        <strong>Valid:</strong> {report.certificate?.valid ? <span className="text-success"><i className="las la-check-circle"></i> Yes</span> : <span className="text-danger"><i className="las la-times-circle"></i> No</span>}
                                    </p>
                                    <p className="mb-0 text-truncate">
                                        <strong>Issuer:</strong> {report.certificate?.issuer?.O || 'Unknown'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Features Checklist */}
                    <div className="col-md-6">
                        <div className="card shadow-sm h-100 border-0 bg-light">
                            <div className="card-body p-4">
                                <h4 className="card-title mb-4 text-dark">
                                    <i className="las la-list-alt me-2"></i>
                                    Comprehensive Protection
                                </h4>
                                <p className="text-muted mb-4">
                                    Unlock the full potential of our security platform. Our detailed reports include:
                                </p>
                                <ul className="list-unstyled">
                                    {[
                                        "Full Port Scanning & Analysis",
                                        "Advanced Malware Detection",
                                        "Detailed Vulnerability Assessment",
                                        "WHOIS & DNS Records",
                                        "SSL/TLS Configuration Deep Dive",
                                        "Traffic Analysis & Monitoring",
                                        "AI-Powered Security Insights",
                                        "PDF Report Generation",
                                        "Historical Scan Data"
                                    ].map((feature, index) => (
                                        <li key={index} className="mb-3 d-flex align-items-center">
                                            <i className="las la-check-circle text-success me-3 fs-4"></i>
                                            <span className="fs-5">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Column: Blurred Login Section */}
                    <div className="col-12 mt-4 position-relative">
                        {/* Blurred Content */}
                        <div style={{ filter: "blur(8px)", opacity: 0.6, pointerEvents: "none", userSelect: "none" }}>
                            <div className="card border-0 shadow-sm">
                                <div className="card-body p-5">
                                    <h3 className="mb-4">Detailed Technical Analysis</h3>
                                    <div className="row">
                                        <div className="col-md-4">
                                            <h5>Open Ports</h5>
                                            <ul className="list-group">
                                                <li className="list-group-item">Port 80 (HTTP) - Open</li>
                                                <li className="list-group-item">Port 443 (HTTPS) - Open</li>
                                                <li className="list-group-item">Port 22 (SSH) - Filtered</li>
                                            </ul>
                                        </div>
                                        <div className="col-md-4">
                                            <h5>Vulnerabilities</h5>
                                            <div className="alert alert-warning">
                                                Potential XSS vector found in header...
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <h5>Server Headers</h5>
                                            <pre className="bg-light p-3 rounded">
                                                Server: nginx/1.18.0
                                                X-Powered-By: Express
                                            </pre>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Overlay */}
                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center z-[10]">
                            <div className="text-center bg-white p-5 rounded shadow-lg border">
                                <i className="las la-lock text-primary display-1 mb-3"></i>
                                <h2 className="fw-bold mb-3">Unlock Full Report</h2>
                                <p className="lead text-muted mb-4">
                                    Login or Register to view detailed analysis and track this domain.
                                </p>
                                <Link
                                    to={`/login?domain=${encodeURIComponent(domain)}`}
                                    className="btn btn-primary btn-lg px-5 pill-btn"
                                >
                                    <i className="las la-sign-in-alt me-2"></i>
                                    Login to View
                                </Link>
                                <div className="mt-3">
                                    <small className="text-muted">
                                        Don't have an account? <Link to="/register">Register here</Link>
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DemoCheck;
